<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

interface Props {
  animation?: 'zoom-in' | 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right'
  duration?: number
  delay?: number
  mirror?: boolean
  threshold?: number
}

const props = withDefaults(defineProps<Props>(), {
  animation: 'fade-up',
  duration: 500,
  delay: 0,
  mirror: false,
  threshold: 0.1,
})

const elementRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const isAnimating = ref(false)
let observer: IntersectionObserver | null = null
let animationTimeout: ReturnType<typeof setTimeout> | null = null

// Map animation types to Tailwind classes
const animationClasses = computed(() => {
  const classes = {
    'zoom-in': {
      initial: 'opacity-0 scale-90',
      animated: 'opacity-100 scale-100',
    },
    'fade-up': {
      initial: 'opacity-0 translate-y-8',
      animated: 'opacity-100 translate-y-0',
    },
    'fade-down': {
      initial: 'opacity-0 -translate-y-8',
      animated: 'opacity-100 translate-y-0',
    },
    'fade-left': {
      initial: 'opacity-0 translate-x-8',
      animated: 'opacity-100 translate-x-0',
    },
    'fade-right': {
      initial: 'opacity-0 -translate-x-8',
      animated: 'opacity-100 translate-x-0',
    },
  }

  return classes[props.animation]
})

// Compute will-change for GPU acceleration
const willChangeValue = computed(() => {
  if (!isAnimating.value)
    return 'auto'
  return 'transform, opacity'
})

onMounted(() => {
  if (!elementRef.value)
    return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isVisible.value) {
          // Use RAF for better timing
          requestAnimationFrame(() => {
            isVisible.value = true
            isAnimating.value = true

            // Remove will-change after animation completes
            animationTimeout = setTimeout(() => {
              isAnimating.value = false
            }, props.duration + props.delay + 50)

            // If mirror is disabled, stop observing
            if (!props.mirror && observer && elementRef.value) {
              observer.unobserve(elementRef.value)
            }
          })
        }
        else if (!entry.isIntersecting && props.mirror && isVisible.value) {
          // Reset animation if mirror is enabled
          isVisible.value = false
          isAnimating.value = false
        }
      })
    },
    {
      threshold: props.threshold,
      rootMargin: '0px 0px -100px 0px',
    },
  )

  observer.observe(elementRef.value)
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (animationTimeout) {
    clearTimeout(animationTimeout)
    animationTimeout = null
  }
})
</script>

<template>
  <div
    ref="elementRef"
    class="transition-all"
    :class="[
      isVisible ? animationClasses.animated : animationClasses.initial,
    ]"
    :style="{
      transitionDuration: `${duration}ms`,
      transitionDelay: `${delay}ms`,
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: willChangeValue,
    }"
  >
    <slot />
  </div>
</template>
